(() => {
  class StickyATC extends HTMLElement {
    connectedCallback() {
      this.variants = this._readVariants();
      this.variantsById = new Map(this.variants.map((v) => [String(v.id), v]));

      this.moneyFormat = this.getAttribute("data-money-format") || "${{amount}}";
      this.currency = this.getAttribute('data-currency') || 'EUR';
      this.root =
        window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : "/";

      // Dawn/Refresh cart components (drawer or notification)
      this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

      // Sticky UI hooks
      this.variantSelect = this.querySelector("[data-sticky-atc-variant]");
      this.priceEl = this.querySelector("[data-sticky-atc-price]");
      this.addBtn = this.querySelector("[data-sticky-atc-add]");

      // Cache original button label
      this._defaultBtnLabel = (this.addBtn?.textContent || "Add to cart").trim();

      // Detect dual-eye product page element (your lenses template)
      this.dual = this._getDualEyeSource();

      // If dual-eye exists, convert sticky UI to 2 selects
      if (this.dual.enabled) {
        this._mountDualEyeUI();
      }

      // Initial selection state
      if (this.dual.enabled) {
        this.leftVariantId = this.leftSelect?.value || "";
        this.rightVariantId = this.rightSelect?.value || "";
      } else {
        this.currentVariantId =
          this.variantSelect?.value || this.getAttribute("data-default-variant-id") || "";
      }

      // Listeners
      if (this.dual.enabled) {
        this.leftSelect?.addEventListener("change", () => {
          this.leftVariantId = this.leftSelect.value;
          this._updateUI();
        });
        this.rightSelect?.addEventListener("change", () => {
          this.rightVariantId = this.rightSelect.value;
          this._updateUI();
        });
      } else {
        this.variantSelect?.addEventListener("change", () => {
          this.currentVariantId = this.variantSelect.value;
          this._updateUI();
        });
      }

      this.addBtn?.addEventListener("click", () => this._addToCart());

      // Show only when main product ATC is out of view
      this._setupVisibilityObserver();

      // Paint initial UI
      this._updateUI();
    }

    /* ----------------------------
     * Data helpers
     * ---------------------------- */

    _readVariants() {
      const node = this.querySelector("[data-sticky-atc-variants]");
      if (!node) return [];
      try {
        return JSON.parse(node.textContent.trim());
      } catch {
        return [];
      }
    }

_formatMoney(cents) {
  if (window.Shopify && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents, this.moneyFormat);
  }

  // Fallback with currency symbol (€, etc.)
  return new Intl.NumberFormat(
    document.documentElement.lang || navigator.language,
    { style: 'currency', currency: this.currency }
  ).format(Number(cents) / 100);
}


    _cartAddUrl() {
      // Prefer theme routes if present, but guarantee ".js"
      const routesUrl = window.routes?.cart_add_url;
      const base = routesUrl || `${this.root}cart/add`;
      return base.endsWith(".js") ? base : `${base}.js`;
    }

    _collectLineItemPropertiesFromMainForm(mainForm) {
      // Copies properties[...] inputs if you use them (optional)
      const props = {};
      if (!mainForm) return props;

      const propInputs = mainForm.querySelectorAll(
        '[name^="properties["], [name^="properties["]'
      );
      propInputs.forEach((el) => {
        if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement))
          return;
        const name = el.name || "";
        const match = name.match(/^properties\[(.+)\]$/);
        if (!match) return;

        const key = match[1];
        let val = "";

        if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
          if (!el.checked) return;
          val = el.value || "on";
        } else {
          val = el.value;
        }

        if (val !== "") props[key] = val;
      });

      return props;
    }

    _getSellingPlanFromMainForm(mainForm) {
      if (!mainForm) return null;
      const sp = mainForm.querySelector('[name="selling_plan"]');
      const val = sp && "value" in sp ? String(sp.value || "") : "";
      return val ? val : null;
    }

    _findMainProductForm() {
      // Refresh/Dawn-style themes generally have a product form posting to /cart/add
      return document.querySelector('main form[action*="/cart/add"]');
    }

    _getDualEyeSource() {
      const dualRoot = document.querySelector("dual-eye-selector");
      if (!dualRoot) return { enabled: false };

      const left = dualRoot.querySelector("[data-left-eye-select]");
      const right = dualRoot.querySelector("[data-right-eye-select]");
      const btn = dualRoot.querySelector("[data-dual-eye-cart-button]");

      // We only enable dual mode if we can find both selects
      if (!(left instanceof HTMLSelectElement) || !(right instanceof HTMLSelectElement)) {
        return { enabled: false };
      }

      return { enabled: true, root: dualRoot, left, right, btn };
    }

    /* ----------------------------
     * UI setup (dual eye)
     * ---------------------------- */

    _mountDualEyeUI() {
      // Reuse existing sticky variant select as LEFT eye select
      if (!this.variantSelect) return;

      this.leftSelect = this.variantSelect;

      // Clone options from the product page selector so variant IDs match automatically per product
      this.leftSelect.innerHTML = this.dual.left.innerHTML;
      this.leftSelect.value = this.dual.left.value || "";
      this.leftSelect.setAttribute("aria-label", "Linkeroog");

      // Create RIGHT eye select by cloning the left select node (keeps classes/styles)
      this.rightSelect = this.leftSelect.cloneNode(true);
      this.rightSelect.innerHTML = this.dual.right.innerHTML;
      this.rightSelect.value = this.dual.right.value || "";
      this.rightSelect.setAttribute("aria-label", "Rechteroog");
      this.rightSelect.setAttribute("data-sticky-atc-right-eye", "");

      // Insert it right after the left select
      this.leftSelect.insertAdjacentElement("afterend", this.rightSelect);

      // Optional: add a small class so you can style dual mode if you want
      this.classList.add("is-dual-eye");
    }

    /* ----------------------------
     * Visibility observer
     * ---------------------------- */

    _setupVisibilityObserver() {
  // Prefer observing the dual-eye add button if present (since some templates hide the default form button)
  const dualBtn = this.dual?.enabled
    ? this.dual.root.querySelector("[data-dual-eye-cart-button]")
    : null;

  const mainForm = this._findMainProductForm();
  const mainButton = mainForm
    ? mainForm.querySelector('button[type="submit"], button[name="add"], input[type="submit"]')
    : null;

  const target = dualBtn || mainButton || mainForm;

  // Footer selector (covers most Shopify themes)
  const footerSelector = "#shopify-section-footer, footer, [data-section-type='footer']";
  let footerEl = document.querySelector(footerSelector);

  // State
  this._mainATCVisible = false;
  this._footerVisible = false;

  const updateVisibility = () => {
    const shouldShow = !this._mainATCVisible && !this._footerVisible;
    this.classList.toggle("is-visible", shouldShow);
  };

  // Fallback without IntersectionObserver
  if (!target || !("IntersectionObserver" in window)) {
    const onScroll = () => {
      const shouldShowAfterScroll = window.scrollY > 300;

      // Hide near the bottom so we don’t cover footer
      const nearBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 220;

      this.classList.toggle("is-visible", shouldShowAfterScroll && !nearBottom);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return;
  }

  // Observe main ATC visibility (when it’s in view, hide sticky)
  const obsMain = new IntersectionObserver(
    (entries) => {
      this._mainATCVisible = !!entries[0]?.isIntersecting;
      updateVisibility();
    },
    { threshold: 0.15 }
  );
  obsMain.observe(target);

  // Observe footer visibility (when footer is close/in view, hide sticky)
  const attachFooterObserver = (el) => {
    if (!el) return;
    const obsFooter = new IntersectionObserver(
      (entries) => {
        this._footerVisible = !!entries[0]?.isIntersecting;
        updateVisibility();
      },
      {
        threshold: 0,
        // Hide a bit BEFORE footer overlaps (tweak 120px if you want earlier/later)
        rootMargin: "0px 0px 120px 0px",
      }
    );
    obsFooter.observe(el);
  };

  if (footerEl) {
    attachFooterObserver(footerEl);
  } else {
    // In case footer loads later (rare), retry once
    setTimeout(() => {
      footerEl = document.querySelector(footerSelector);
      attachFooterObserver(footerEl);
    }, 400);
  }

  // Initial paint
  updateVisibility();
}


    /* ----------------------------
     * UI update
     * ---------------------------- */

    _updateUI() {
      if (!this.addBtn) return;

      if (this.dual.enabled) {
        const leftId = String(this.leftVariantId || "");
        const rightId = String(this.rightVariantId || "");
        const leftV = leftId ? this.variantsById.get(leftId) : null;
        const rightV = rightId ? this.variantsById.get(rightId) : null;

        const leftOk = !!leftId && !!leftV && leftV.available;
        const rightOk = !!rightId && !!rightV && rightV.available;

        // Button state
        this.addBtn.disabled = !(leftOk && rightOk);

        // Button label
        this.addBtn.textContent = this.addBtn.disabled ? this._defaultBtnLabel : this._defaultBtnLabel;

        // Price: show combined total if we can resolve both variants
        if (this.priceEl && leftV && rightV) {
          const totalCents =
            leftV.id === rightV.id ? leftV.price * 2 : leftV.price + rightV.price;

          this.priceEl.textContent = this._formatMoney(totalCents);
        }

        return;
      }

      // Single-variant mode
      const v = this.variantsById.get(String(this.currentVariantId));
      if (!v) return;

      if (this.priceEl) {
        const price = this._formatMoney(v.price);
        const compare =
          v.compare_at_price && v.compare_at_price > v.price
            ? this._formatMoney(v.compare_at_price)
            : null;
        this.priceEl.textContent = compare ? `${price} (was ${compare})` : price;
      }

      this.addBtn.disabled = !v.available;
      this.addBtn.textContent = v.available ? this._defaultBtnLabel : "Sold out";
    }

    /* ----------------------------
     * Add to cart
     * ---------------------------- */

    async _addToCart() {
      if (!this.addBtn) return;

      if (this.dual.enabled) return this._addDualToCart();
      return this._addSingleToCart();
    }

    async _addSingleToCart() {
      const v = this.variantsById.get(String(this.currentVariantId));
      if (!v || !v.available) return;

      const mainForm = this._findMainProductForm();
      const formData = mainForm ? new FormData(mainForm) : new FormData();

      // Force our selected variant
      formData.set("id", String(v.id));
      if (!formData.has("quantity")) formData.set("quantity", "1");

      // Dawn-style: request updated cart sections so cart drawer can re-render
      if (this.cart && typeof this.cart.getSectionsToRender === "function") {
        formData.set(
          "sections",
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.set("sections_url", window.location.pathname);
        if (typeof this.cart.setActiveElement === "function") {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      this.addBtn.disabled = true;

      try {
        const res = await fetch(this._cartAddUrl(), {
          method: "POST",
          headers: { "X-Requested-With": "XMLHttpRequest" },
          body: formData,
          credentials: "same-origin",
        });

        const data = await res.json().catch(() => null);

        if (!res.ok || (data && data.status)) {
          alert(data?.description || data?.message || "Could not add to cart.");
          return;
        }

        this._afterAdd(data);
      } catch (e) {
        console.error(e);
        alert("Network error adding to cart.");
      } finally {
        this.addBtn.disabled = false;
        this._updateUI();
      }
    }

    async _addDualToCart() {
      const leftId = String(this.leftVariantId || "");
      const rightId = String(this.rightVariantId || "");

      const leftV = leftId ? this.variantsById.get(leftId) : null;
      const rightV = rightId ? this.variantsById.get(rightId) : null;

      if (!leftV || !rightV || !leftV.available || !rightV.available) return;

      const mainForm = this._findMainProductForm();
      const sellingPlan = this._getSellingPlanFromMainForm(mainForm);
      const props = this._collectLineItemPropertiesFromMainForm(mainForm);

      // If same variant selected for both eyes, send quantity 2 as a single line item
      const items =
        leftId === rightId
          ? [{ id: Number(leftId), quantity: 2, ...(sellingPlan ? { selling_plan: sellingPlan } : {}), ...(Object.keys(props).length ? { properties: props } : {}) }]
          : [
              { id: Number(leftId), quantity: 1, ...(sellingPlan ? { selling_plan: sellingPlan } : {}), ...(Object.keys(props).length ? { properties: props } : {}) },
              { id: Number(rightId), quantity: 1, ...(sellingPlan ? { selling_plan: sellingPlan } : {}), ...(Object.keys(props).length ? { properties: props } : {}) },
            ];

      const body = { items };

      // Dawn-style section rendering (so cart drawer can refresh immediately)
      if (this.cart && typeof this.cart.getSectionsToRender === "function") {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === "function") {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      this.addBtn.disabled = true;

      try {
        const res = await fetch(this._cartAddUrl(), {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
          credentials: "same-origin",
        });

        const data = await res.json().catch(() => null);

        if (!res.ok || (data && data.status)) {
          alert(data?.description || data?.message || "Could not add to cart.");
          return;
        }

        this._afterAdd(data);
      } catch (e) {
        console.error(e);
        alert("Network error adding to cart.");
      } finally {
        this.addBtn.disabled = false;
        this._updateUI();
      }
    }

    _afterAdd(response) {
      // If we have a Dawn-style cart component, re-render it with returned sections
      if (this.cart && typeof this.cart.renderContents === "function") {
        this.cart.renderContents(response);
      }

      // Open drawer/notification if available
      const drawer = document.querySelector("cart-drawer");
      const notification = document.querySelector("cart-notification");
      if (drawer && typeof drawer.open === "function") drawer.open();
      if (notification && typeof notification.open === "function") notification.open();

      // Lightweight “nudge” for any listeners
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { added: response } }));
    }
  }

  if (!customElements.get("sticky-atc")) {
    customElements.define("sticky-atc", StickyATC);
  }
})();
